import { useQueries } from '@tanstack/react-query';
import { useQueryCacheManager } from '../../hooks/useQueryCacheManager';
import useApi from '../../api/backendApiContext';
import { ProgramAttachment } from '../types';
import { ProgramApi } from '../programApi';

export type ProgramAttachmentWithUrl = ProgramAttachment & {
  url?: string;
};

const getFileUrl = async (
  programApi: ProgramApi,
  programId: string,
  attachment: ProgramAttachment
) => {
  return attachment.file
    ? window.URL.createObjectURL(attachment.file)
    : (await programApi.generateProgramAttachmentUrl(programId, attachment.id, false)).url;
};

export const createAttachmentContentQueryParams = (
  programApi: ProgramApi,
  queryKey: string[],
  programId: string,
  attachment: ProgramAttachment,
  isEnabled: boolean
) => {
  return {
    enabled: isEnabled,
    queryKey: queryKey,
    queryFn: async (): Promise<ProgramAttachmentWithUrl> => {
      const url = await getFileUrl(programApi, programId, attachment);
      return { ...attachment, url };
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: 0,
  };
};

export const useAttachmentsContent = (
  programId: string,
  attachments: ProgramAttachment[],
  isEnabled?: (index: number) => boolean
) => {
  const cacheManager = useQueryCacheManager();
  const { programApi } = useApi();

  return useQueries({
    queries: attachments.map((attachment, index) =>
      createAttachmentContentQueryParams(
        programApi,
        cacheManager.getProgramAttachmentContentCacheName(programId, attachment.id),
        programId,
        attachment,
        isEnabled === undefined || isEnabled(index)
      )
    ),
  });
};

export const useAttachmentContent = (programId: string, attachment: ProgramAttachment) =>
  useAttachmentsContent(programId, [attachment])[0];
